import React from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Avatar,
  Pagination,
  TextField,
  Alert
} from '@mui/material'
import {
  ArrowForward as ArrowForwardIcon,
  CalendarToday as CalendarIcon
} from '@mui/icons-material'
import SEO from '@components/SEO'

const BlogPage: React.FC = () => {
  const navigate = useNavigate()
  const blogPosts = [
    {
      id: 1,
      title: 'The Complete Guide to Cigar Storage and Humidor Management',
      excerpt: 'Learn everything you need to know about proper cigar storage, humidor maintenance, and creating the perfect environment for your collection.',
      author: 'David O\'Hara',
      authorAvatar: 'DO',
      publishDate: '2026-03-24',
      readTime: '8 min read',
      category: 'Storage',
      image: '',
      featured: true
    },
    {
      id: 2,
      title: 'Understanding Cigar Ratings: A Beginner\'s Guide',
      excerpt: 'Discover how to properly rate and review cigars, what factors to consider, and how to develop your palate for better tasting experiences.',
      author: 'Sarah Chen',
      authorAvatar: 'SC',
      publishDate: '2026-03-10',
      readTime: '6 min read',
      category: 'Tasting',
      image: '',
      featured: false
    },
    {
      id: 3,
      title: 'Building Your First Cigar Collection: Tips for Beginners',
      excerpt: 'Starting a cigar collection can be overwhelming. Here\'s a comprehensive guide to help you build a diverse and enjoyable collection.',
      author: 'Michael Rodriguez',
      authorAvatar: 'MR',
      publishDate: '2026-02-24',
      readTime: '10 min read',
      category: 'Collection',
      image: '',
      featured: false
    },
    {
      id: 4,
      title: 'The Art of Cigar Pairing: Food, Drinks, and More',
      excerpt: 'Explore the world of cigar pairing and discover how different foods, drinks, and environments can enhance your smoking experience.',
      author: 'Jennifer Kim',
      authorAvatar: 'JK',
      publishDate: '2026-02-10',
      readTime: '7 min read',
      category: 'Pairing',
      image: '',
      featured: false
    },
    {
      id: 5,
      title: 'Cigar Aging: When and How to Age Your Cigars',
      excerpt: 'Learn about the aging process, optimal aging conditions, and how to determine when your cigars are ready to smoke.',
      author: 'David O\'Hara',
      authorAvatar: 'DO',
      publishDate: '2026-01-27',
      readTime: '9 min read',
      category: 'Aging',
      image: '',
      featured: false
    },
    {
      id: 6,
      title: 'Top 10 Cigar Brands Every Collector Should Know',
      excerpt: 'Discover the most prestigious and popular cigar brands, their history, and what makes them special in the world of premium cigars.',
      author: 'Sarah Chen',
      authorAvatar: 'SC',
      publishDate: '2026-01-13',
      readTime: '12 min read',
      category: 'Brands',
      image: '',
      featured: false
    }
  ]

  const categories = ['All', 'Storage', 'Tasting', 'Collection', 'Pairing', 'Aging', 'Brands']
  const [selectedCategory, setSelectedCategory] = React.useState('All')
  const [newsletterEmail, setNewsletterEmail] = React.useState('')
  const [newsletterSubmitted, setNewsletterSubmitted] = React.useState(false)
  const [newsletterError, setNewsletterError] = React.useState('')

  const handleNewsletterSubscribe = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!newsletterEmail.trim()) {
      setNewsletterError('Please enter your email address.')
      return
    }
    if (!emailRegex.test(newsletterEmail.trim())) {
      setNewsletterError('Please enter a valid email address.')
      return
    }
    setNewsletterError('')
    setNewsletterSubmitted(true)
  }

  const filteredPosts = selectedCategory === 'All' 
    ? blogPosts 
    : blogPosts.filter(post => post.category === selectedCategory)

  const featuredPost = blogPosts.find(post => post.featured)
  const regularPosts = filteredPosts.filter(post => !post.featured)

  const postsPerPage = 6
  const totalPages = Math.ceil(regularPosts.length / postsPerPage)

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  return (
    <Box>
      <SEO
        title="Blog - CigarTrack"
        description="Tips, guides, and news for cigar enthusiasts. Explore articles on collection management, humidor care, tasting notes, and more from the CigarTrack blog."
        keywords="cigar blog, cigar tips, humidor guide, cigar reviews, cigar enthusiast"
        url="/blog"
      />
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #8B4513 0%, #DAA520 100%)',
          color: 'white',
          py: { xs: 6, md: 8 }
        }}
      >
        <Container maxWidth="lg">
          <Box textAlign="center">
            <Typography
              variant="h2"
              component="h1"
              gutterBottom
              sx={{
                fontWeight: 700,
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                lineHeight: 1.2
              }}
            >
              CigarTrack Blog
            </Typography>
            <Typography
              variant="h5"
              sx={{
                mb: 4,
                opacity: 0.9,
                fontSize: { xs: '1.1rem', md: '1.3rem' },
                maxWidth: 600,
                mx: 'auto'
              }}
            >
              Expert insights, tips, and guides to help you get the most out of your cigar collection and smoking experience.
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Category Filter */}
      <Box sx={{ py: 4, backgroundColor: '#F5F5DC' }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'center' }}>
            {categories.map((category) => (
              <Chip
                key={category}
                label={category}
                onClick={() => setSelectedCategory(category)}
                variant={selectedCategory === category ? 'filled' : 'outlined'}
                sx={{
                  backgroundColor: selectedCategory === category ? '#8B4513' : 'transparent',
                  color: selectedCategory === category ? 'white' : '#8B4513',
                  borderColor: '#8B4513',
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: selectedCategory === category ? '#7A3D0F' : 'rgba(139, 69, 19, 0.1)'
                  }
                }}
              />
            ))}
          </Box>
        </Container>
      </Box>

      {/* Featured Post */}
      {featuredPost && selectedCategory === 'All' && (
        <Box sx={{ py: 6 }}>
          <Container maxWidth="lg">
            <Typography
              variant="h4"
              component="h2"
              gutterBottom
              sx={{ color: '#8B4513', fontWeight: 600, mb: 4 }}
            >
              Featured Article
            </Typography>
            <Card
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                height: { xs: 'auto', md: 400 },
                overflow: 'hidden'
              }}
            >
              <CardMedia
                component="div"
                sx={{
                  width: { xs: '100%', md: '50%' },
                  height: { xs: 250, md: '100%' },
                  backgroundColor: '#f8f9fa',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <Typography variant="h6" color="text.secondary">
                  Featured Image
                </Typography>
              </CardMedia>
              <CardContent sx={{ flex: 1, p: 4, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <Chip
                  label={featuredPost.category}
                  size="small"
                  sx={{
                    backgroundColor: '#8B4513',
                    color: 'white',
                    mb: 2,
                    width: 'fit-content'
                  }}
                />
                <Typography
                  variant="h4"
                  component="h3"
                  gutterBottom
                  sx={{ color: '#8B4513', fontWeight: 600 }}
                >
                  {featuredPost.title}
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ mb: 3, lineHeight: 1.7 }}
                >
                  {featuredPost.excerpt}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Avatar sx={{ width: 40, height: 40, mr: 2, bgcolor: '#8B4513' }}>
                    {featuredPost.authorAvatar}
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle2" fontWeight="600">
                      {featuredPost.author}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Typography variant="caption" color="text.secondary">
                        <CalendarIcon sx={{ fontSize: 14, mr: 0.5 }} />
                        {formatDate(featuredPost.publishDate)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {featuredPost.readTime}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                <Button
                  variant="contained"
                  endIcon={<ArrowForwardIcon />}
                  onClick={() => navigate(`/blog/${featuredPost.id}`)}
                  sx={{
                    backgroundColor: '#8B4513',
                    color: 'white',
                    alignSelf: 'flex-start',
                    '&:hover': {
                      backgroundColor: '#7A3D0F'
                    }
                  }}
                >
                  Read More
                </Button>
              </CardContent>
            </Card>
          </Container>
        </Box>
      )}

      {/* Blog Posts Grid */}
      <Box sx={{ py: 6 }}>
        <Container maxWidth="lg">
          <Typography
            variant="h4"
            component="h2"
            gutterBottom
            sx={{ color: '#8B4513', fontWeight: 600, mb: 4 }}
          >
            {selectedCategory === 'All' ? 'All Articles' : `${selectedCategory} Articles`}
          </Typography>
          <Grid container spacing={4}>
            {regularPosts.map((post) => (
              <Grid item xs={12} sm={6} md={4} key={post.id}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'transform 0.2s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 4
                    }
                  }}
                >
                  <CardMedia
                    component="div"
                    sx={{
                      height: 200,
                      backgroundColor: '#f8f9fa',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Article Image
                    </Typography>
                  </CardMedia>
                  <CardContent sx={{ flexGrow: 1, p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Chip
                        label={post.category}
                        size="small"
                        sx={{
                          backgroundColor: '#8B4513',
                          color: 'white',
                          mr: 2
                        }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {post.readTime}
                      </Typography>
                    </Box>
                    <Typography
                      variant="h6"
                      component="h3"
                      gutterBottom
                      sx={{
                        color: '#8B4513',
                        fontWeight: 600,
                        lineHeight: 1.3,
                        mb: 2
                      }}
                    >
                      {post.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 3, lineHeight: 1.6 }}
                    >
                      {post.excerpt}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Avatar sx={{ width: 32, height: 32, mr: 2, bgcolor: '#8B4513' }}>
                        {post.authorAvatar}
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle2" fontWeight="600">
                          {post.author}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(post.publishDate)}
                        </Typography>
                      </Box>
                    </Box>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => navigate(`/blog/${post.id}`)}
                      sx={{
                        borderColor: '#8B4513',
                        color: '#8B4513',
                        '&:hover': {
                          borderColor: '#7A3D0F',
                          backgroundColor: 'rgba(139, 69, 19, 0.1)'
                        }
                      }}
                    >
                      Read More
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
              <Pagination
                count={totalPages}
                color="primary"
                sx={{
                  '& .MuiPaginationItem-root': {
                    color: '#8B4513'
                  },
                  '& .Mui-selected': {
                    backgroundColor: '#8B4513',
                    color: 'white'
                  }
                }}
              />
            </Box>
          )}
        </Container>
      </Box>

      {/* Newsletter Signup */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #8B4513 0%, #DAA520 100%)',
          color: 'white',
          py: 8
        }}
      >
        <Container maxWidth="md">
          <Box textAlign="center">
            <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
              Stay Updated
            </Typography>
            <Typography variant="h6" sx={{ mb: 4, opacity: 0.9 }}>
              Subscribe to our newsletter for the latest articles, tips, and CigarTrack updates.
            </Typography>
            {newsletterSubmitted ? (
              <Alert
                severity="success"
                sx={{
                  maxWidth: 480,
                  mx: 'auto',
                  backgroundColor: 'rgba(255,255,255,0.15)',
                  color: 'white',
                  '& .MuiAlert-icon': { color: 'white' }
                }}
              >
                Thanks for subscribing! We&apos;ll be in touch.
              </Alert>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: 2,
                  justifyContent: 'center',
                  alignItems: 'flex-start',
                  maxWidth: 520,
                  mx: 'auto'
                }}
              >
                <TextField
                  type="email"
                  placeholder="Enter your email address"
                  value={newsletterEmail}
                  onChange={(e) => {
                    setNewsletterEmail(e.target.value)
                    if (newsletterError) setNewsletterError('')
                  }}
                  error={!!newsletterError}
                  helperText={newsletterError}
                  size="small"
                  sx={{
                    flex: 1,
                    minWidth: 240,
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'white',
                      borderRadius: 1
                    },
                    '& .MuiFormHelperText-root': {
                      color: '#FFD580'
                    }
                  }}
                  inputProps={{ 'aria-label': 'newsletter email' }}
                />
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleNewsletterSubscribe}
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    backgroundColor: 'white',
                    color: '#8B4513',
                    px: 4,
                    py: 1,
                    fontSize: '1rem',
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                    '&:hover': {
                      backgroundColor: '#f5f5f5'
                    }
                  }}
                >
                  Subscribe Now
                </Button>
              </Box>
            )}
          </Box>
        </Container>
      </Box>
    </Box>
  )
}

export default BlogPage

